import React, { useState } from "react"
import { type ViewLocation, view_to_grid } from "../../../../../plate_viewer/coords"
import Nana from "../../../../../globals"
import * as Commands from "../../../../../model/commands"
import { Idea, HexColor, IdeaType, ID, Project } from "../../../../../model/types"

import { useNana } from "../../../../hooks/useNana"
import FuzzyProjectFinder from "../../../../components/fuzzy_project_finder"
import { NavigationHistoryState } from "../../mosaic"
import { generatePublicId } from "../../../../../model/public_id"

type ContextMenuState = null | {
  at: ViewLocation
  on: "idea" | "blank"
  colour?: HexColor
  targetedIdeaIds?: ID[] | null
}

const ContextMenu = ({ state }: { state: ContextMenuState }) => {
  const { model, commander, plateView } = useNana()
  const [fuzzyFinderOpen, setFuzzyFinderOpen] = useState(false)

  if (!state) return null

  const mostRecentProject = model.getMostRecentProject()

  const actionAddIdea = (type: IdeaType) => {
    if (!plateView) return

    const loc = view_to_grid(state.at, plateView.camera_state())

    if (!model.getIdeaAt(loc)) {
      const nearestIdea = model.getNearest(loc)
      const id = generatePublicId()
      const now = new Date()
      commander.exec(
        new Commands.CreateIdeaCommand({
          id,
          idea: {
            id,
            type,
            memo: "",
            colour: nearestIdea?.colour || ("#0000FF" as HexColor),
            title: "",
            content: "",
            location: loc,
            sourceType: null,
            sourceId: null,
            connections: [],
            createdAt: now,
            updatedAt: now,
            editedAt: now,
            lastViewedAt: null,
          },
        }),
      )
      NavigationHistoryState.set_open_location(loc)
    }
    plateView.closeContextMenu()
  }

  const actionColour = (colour: string) => {
    if (!plateView) return
    const ids = state.targetedIdeaIds
    if (!ids) return
    const pairs: [ID, HexColor][] = []
    ids.forEach((id) => {
      pairs.push([id, colour as HexColor])
    })
    commander.exec(new Commands.ColourIdeasCommand({ idColours: pairs }))
  }

  const actionDelete = () => {
    if (!plateView) return
    const ids = state.targetedIdeaIds
    if (!ids) return
    commander.exec(new Commands.DeleteIdeasCommand({ ids }))
    plateView.clearSelectedIdeas()
    plateView.closeContextMenu()
  }

  const actionAddToProject = (project: Project) => {
    if (!plateView) return
    if (!state.targetedIdeaIds) return

    commander.exec(
      new Commands.SetProjectFieldsCommand({
        id: project.id,
        fields: {
          ideaIds: project.ideaIds.concat(state.targetedIdeaIds),
        },
      }),
    )
    plateView.closeContextMenu()
  }

  return (
    <div
      className="absolute top-0 left-0  w-32 rounded-sm bg-white dark:bg-gray-800 border dark:border-gray-700 shadow-md border-gray-200  dark:text-gray-200 text-sm"
      style={{
        left: `${state.at.vx}px`,
        top: `${state.at.vy}px`,
      }}
      data-testid="context-menu"
    >
      {state.on === "blank" && (
        <div
          onClick={() => actionAddIdea("idea")}
          className="cursor-pointer p-1 hover:bg-sky-100 dark:hover:bg-sky-900"
        >
          Add idea
        </div>
      )}
      {state.on === "blank" && (
        <div
          onClick={() => actionAddIdea("goal")}
          className="cursor-pointer p-1 hover:bg-sky-100 dark:hover:bg-sky-900"
        >
          Add goal
        </div>
      )}
      {state.on === "idea" && (
        <div
          id="paint-colour-button-ignore"
          className="cursor-pointer hover:bg-sky-100 dark:hover:bg-sky-900 flex justify-stretch items-stretch"
        >
          <label className="flex-grow cursor-pointer p-1">
            Colour
            <input
              type="color"
              id="paint-colour-button"
              className="invisible cursor-pointer h-4"
              value={state.colour}
              onChange={(e) => actionColour(e.target.value)}
            />
          </label>
        </div>
      )}

      {state.on === "idea" && (
        <div
          id="delete-in-plate-view-button"
          className="cursor-pointer p-1 hover:bg-sky-100 dark:hover:bg-sky-900"
          onClick={() => actionDelete()}
        >
          Delete
        </div>
      )}

      {state.on === "idea" && mostRecentProject && (
        <>
          <div
            className="cursor-pointer p-1 hover:bg-sky-100 dark:hover:bg-sky-900"
            onClick={() => setFuzzyFinderOpen(true)}
          >
            Add to project
          </div>
          {fuzzyFinderOpen && (
            <FuzzyProjectFinder
              title="Select project to add idea(s) to"
              includeCompleted={false}
              chooseProject={(project) => {
                actionAddToProject(project)
              }}
              close={() => setFuzzyFinderOpen(false)}
            />
          )}
        </>
      )}
    </div>
  )
}

export default ContextMenu
export { ContextMenuState }
